import type { NudgeState, PreferencesResponse, PreferencesUpdate } from "@lms/shared-types";
import { useEffect, useId, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { fetchJson } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { passkeysPossible } from "../auth/cognito-api";
import { usePasskeyCount } from "../auth/usePasskeys";
import { useAnnounce } from "../lib/announce";
import { answer, signInId, visibleNudge } from "../lib/nudges";
import { btnPrimary, Button, ErrorState } from "./ui";

const ANNOUNCED_KEY = "lms.nudgeAnnounced";

/**
 * One nudge at a time, above the page (lib/nudges.ts), mounted once in the
 * layout so a route change never re-announces it. A labelled region the
 * person can land on and come back to — the LMS's asks are not transient
 * (templates-port-plan.md §6) — and spoken once per sign-in through the one
 * live region. "Do later" comes back at the next sign-in; "Dismiss forever"
 * does not, and is offered only where the ask may be refused; the primary
 * action goes where the thing is done, and the nudge goes when it is.
 */
export function NudgeBanner() {
  const { me, session, config, refreshMe } = useAuth();
  const { pathname } = useLocation();
  const announce = useAnnounce();
  const passkeyCount = usePasskeyCount();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const titleId = useId();
  const facts = { ...me?.facts, passkeyCount, passkeysPossible: passkeysPossible() };
  const nudge = me ? visibleNudge(me, me.preferences, session, facts, pathname) : undefined;
  const body = nudge?.body(facts);

  // Spoken once per sign-in and nudge, not on every page it follows the person to.
  useEffect(() => {
    if (!nudge || !body) return;
    const mark = `${signInId(session) ?? "-"}:${nudge.id}`;
    try {
      if (sessionStorage.getItem(ANNOUNCED_KEY) === mark) return;
      sessionStorage.setItem(ANNOUNCED_KEY, mark);
    } catch {
      // No storage: say it anyway.
    }
    announce(`${nudge.title}. ${body}`);
  }, [nudge, body, session, announce]);

  if (!nudge || !me || !session) return null;

  const respond = async (state: NudgeState["state"]) => {
    setBusy(true);
    setError(undefined);
    try {
      // This one answer only: the server merges it into the map, so a stale /v1/me here cannot undo an answer given elsewhere.
      const update: PreferencesUpdate = { nudges: { [nudge.id]: answer(state, session) } };
      await fetchJson<PreferencesResponse>(config, session, "/v1/me/preferences", { method: "PATCH", body: JSON.stringify(update), headers: { "content-type": "application/json" } });
      await refreshMe();
      // The banner — and the button that had focus — is gone: put focus somewhere a screen reader can continue from.
      document.getElementById("main")?.focus();
      announce(state === "later" ? `${nudge.title}: hidden until you next sign in.` : `${nudge.title}: dismissed.`);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby={titleId} data-testid="nudge" data-nudge={nudge.id} className="border-b border-line bg-surface">
      <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center gap-x-4 gap-y-3 border-l-4 border-l-accent px-4 py-3 text-sm lg:px-10">
        <p className="min-w-0 flex-1 basis-72 text-ink">
          <strong id={titleId}>{nudge.title}.</strong> {body}
        </p>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Link to={nudge.action.to} className={btnPrimary}>
            {nudge.action.label}
          </Link>
          <Button disabled={busy} onClick={() => void respond("later")}>
            Do later
          </Button>
          {nudge.dismissible && (
            <Button disabled={busy} onClick={() => void respond("never")}>
              Dismiss forever
            </Button>
          )}
        </div>
        {error !== undefined && (
          <div className="w-full">
            <ErrorState title="Your answer could not be saved." error={error} />
          </div>
        )}
      </div>
    </section>
  );
}
